import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const faqs = [
  {
    q: "How is SmartPlaite different from MyFitnessPal or Whoop?",
    a: "MyFitnessPal tracks calories. Whoop tracks recovery. SmartPlaite runs one AI brain across both — plus your meals, training, sleep, peptides, and biometrics — and turns it into a single MIQ score personalized to your specific goal.",
  },
  {
    q: "What's the difference between MIQ and a calorie count?",
    a: "Calorie apps tell you what you ate. MIQ tells you how your whole system — nutrition, training, sleep, supplements, biometrics — is actually performing against your specific goal. One score. Personal to you. Updates in real time.",
  },
  {
    q: "Which wearables and integrations do you support?",
    a: "Apple Health, Google Fit, Fitbit, Garmin, Whoop, Oura, and Samsung Health. Wearable sync is a Genius-tier feature — Free and Smart give you live MIQ, photo logging, daily check-ins and micronutrient tracking without needing a wearable connected.",
  },
  {
    q: "Can SmartPlaite read my actual lab results?",
    a: "Yes — and it's the killer feature on Genius. Snap a photo or upload the PDF your doctor sent you. Our AI reads 15+ markers (LDL, HDL, triglycerides, HbA1c, glucose, vitamin D, B12, testosterone, hsCRP, ferritin, TSH, and more), groups them by category, and folds them into your MIQ Confidence score with a +15% boost on fresh draws (<90 days old). Every flagged result gets a \"Why this matters\" tooltip in plain English, and your Smart Coach starts making bloodwork-aware suggestions the second your labs save. We support multi-page PDFs and multi-photo uploads — and yes, low LDL is labeled \"Favorable,\" not \"Low.\"",
  },
  {
    q: "Can I log my meals in Spanish, French, or Mandarin?",
    a: "Yes — in 50+ languages, auto-detected. Hold the mic, say what you ate, and our voice engine (powered by OpenAI Whisper) transcribes it in whatever language you're speaking. You can edit the transcript before it logs — useful for catching brand names or local foods the model might miss. Voice works for meal logging AND Smart Coach questions. Smart tier gets 5 voice logs/day; Genius is unlimited.",
  },
  {
    q: "What's the \"restaurant-aware\" thing — does it really know Chipotle?",
    a: "Yes. SmartPlaite's Health Intelligence knows the live menu at 500K+ restaurants — Chipotle, Sweetgreen, Chick-fil-A, Cava, Panera, your local sushi spot. Ask in plain English (\"I'm at Chipotle — based on my remaining macros, what should I order?\") and it returns the exact bowl, modifiers, and macro hit. Confirm to auto-log.",
  },
  {
    q: "Is my health data private?",
    a: "Yes. Data is encrypted end-to-end, never sold or shared, and you can export or delete everything anytime. You own the signal — we just run the intelligence on it.",
  },
  {
    q: "Telehealth — is it really available on the Free tier?",
    a: "Yes. Every SmartPlaite user — Free, Smart, or Genius — can tap to connect with a licensed clinician for on-demand medical guidance: lab review, prescriptions, symptom checks, and peptide & supplement consults. We believe access to real medical advice shouldn't be paywalled. Visit fees may apply per state and provider.",
  },
  {
    q: "Do I need to log everything manually?",
    a: "No. SmartPlaite auto-pulls from wearables and uses photo + voice meal logging. Average user spends under 90 seconds per day in the app.",
  },
  {
    q: "What happens after the 7-day Genius trial?",
    a: "You can stay on Genius ($9.99/mo or $83.99/yr prepaid — save 30%), drop to Smart ($4.99/mo or $41.99/yr), or stay on Free forever. No credit card needed to start. Cancel anytime.",
  },
];

export default function FAQ() {
  return (
    <section id="faq" data-testid="faq-section" className="relative py-28 md:py-36">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-brand-accent font-bold mb-5 text-center">FAQ</div>
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02] text-center">
            Real questions, <span className="text-gradient-hero">real answers</span>.
          </h2>
        </div>

        <Accordion type="single" collapsible className="mt-14 space-y-3">
          {faqs.map((f, i) => (
            <AccordionItem
              key={i}
              value={`faq-${i}`}
              data-testid={`faq-item-${i + 1}`}
              className="glass rounded-2xl px-5 border-0"
            >
              <AccordionTrigger className="text-left text-base sm:text-lg font-display font-semibold tracking-tight hover:no-underline py-5">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-white/65 text-sm sm:text-base leading-relaxed font-light pb-5">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
